const { formatMoney, getElement, getElements } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
  inventoryStatus: '[data-product-inventory-status]',
  miniBagSizeLabel: '[data-mini-bag-size-label]',
  optionLabel: '[data-product-option-label]',
}

class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }


  setPrice () {
    unhidePriceEls()

    const priceElements = getElements(selectors.price)
    priceElements.forEach((el) => {
      el.innerHTML = formatMoney(this.data.price)
    })

    const priceCompareElements = getElements(selectors.priceCompare)
    priceCompareElements.forEach(
      (el) => (el.innerHTML = formatMoney(this.data.compare_at_price)),
    )

    if (!this.data.compare_at_price) {
      hidePriceCompareElements()
    }
  }

  setAvailability () {

    getElements(selectors.optionLabel).forEach(el => {
      const radioEl = getElement('[data-variant-radios-el="option-choice"]', { context: el.parentElement })
      if (!radioEl || !radioEl?.checked) return;

      el.setAttribute('data-available', this.data.available)
    })

    getElements(selectors.addToCart).forEach(el => {
      //el.disabled = !this.data.available;
      el.dataset.id = this.data.id;
      el.classList.toggle('is-oos', !this.data.available);
    })

    getElements(selectors.addToCartText).forEach(
      (el) =>
        (el.textContent = this.data.available
          ? this.getCtaTitle(el)
          : window.theme.strings.unavailable),
    )
  }

  getCtaTitle(el) {
    return el.hasAttribute('data-pre-order') ? window.theme.strings.preOrder : window.theme.strings.addToCart
  }

  setMiniBagSizeLabel () {
    getElements(selectors.miniBagSizeLabel).forEach(
      (el) => (el.textContent = this.data.title),
    )
  }

  setInventoryStatus() {
    const inventoryElements = getElements(selectors.inventoryStatus)
    const inventoryDataElement = document.querySelector('script[data-variant-inventory]')
    const inventoryData = JSON.parse(inventoryDataElement.textContent)
    const fewLimit = parseInt(inventoryData.few_limit, 10)
    const variantsArray = inventoryData.variants

    const matchingVariant = variantsArray.find(variant => {
      return variant.id === this.data.id.toString()
    })

    if (matchingVariant) {
        const inventoryQuantity = matchingVariant.inventory_quantity

        inventoryElements.forEach((el) => {
          if(inventoryQuantity === 0){
            el.textContent = window.theme.strings.soldOut;
          }
          else if (inventoryQuantity <= fewLimit) {
            el.textContent = window.theme.strings.lowStockText;
          }
          else
            el.textContent = ``
        });
    }
  }

  updateInput () {
    const inputs = getElements(`form[action*="/cart/add"] [name="id"]`)

    inputs.forEach((input) => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange () {
    const variant = this.getVariant()
    variant.setAvailability()
    variant.setPrice()
    variant.updateInput()
    variant.setInventoryStatus()
    variant.setMiniBagSizeLabel()

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      (variant) => variant.id === this?.currentVariant?.id,
    )
    if (existingVariant) return existingVariant

    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError(`selectedOptions needs to be implemented on this class`)

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find((variant) => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find((el) => el.checked).value

      return {
        label: name,
        value: choice,
      }
    })
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions () {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios)
}

function hideElements (elements) {
  elements.forEach((el) => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach((el) => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
}

function hidePriceCompareElements () {
  hideElements(getElements(selectors.priceCompare))
}
